import {
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
    DropdownData,
    InputType,
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { list } from 'src/app/common/classes/request-builder';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { TrasiegoModel } from 'src/app/models/visitas/trasiego.model';

const FIRST_WEEK = 1;
const LAST_WEEK = 54;
const FIRST_YEAR = 2020;
const LAST_YEAR = 2030;
const DEFAULT_PEST_ROWS = 6;
const EXTENDED_PEST_ROWS = 10;
const DEFAULT_FIELD_WIDTH = 3;
const HALF_ROW_FIELD_WIDTH = 6;

export class VisitasTrasiegoListsDefinition {
    public fincas: DropdownData<FincasModel> = list<FincasModel>(['...', null]);

    public parcelas: DropdownData<ParcelasModel> = list<ParcelasModel>(['...', null]);

    public semanas: DropdownData<number> = list<number>(
        ['...', null],
        ...Array.from({ length: LAST_WEEK - FIRST_WEEK + 1 }, (_item, index) => FIRST_WEEK + index),
    );

    public anos: DropdownData<number> = list<number>(
        ['...', null],
        ...Array.from({ length: LAST_YEAR - FIRST_YEAR + 1 }, (_item, index) => FIRST_YEAR + index),
    );

    public plagas: DropdownData<string> = list<string>(
        ['...', null],
        'Empoasca',
        'Mosca blanca',
        'Mosca común',
        'Mosquito',
        'Parasitoides',
        'Pulgón',
        'Scatella',
        'Sciara',
        'Stethorus',
        'Trips',
        'Otros',
    );

    public trampas: DropdownData<string> = list<string>(
        ['...', null],
        'T.S1',
        'T.S2',
        'T.S3',
        'T.S4',
        'T.SEMILLERO',
        'T.S5',
        'T.S6',
        'T.S7',
        'T.S8',
        'T.S9',
        'T.S10',
        'T.MICROS',
        'FC-T.S1',
        'FC-T.S2',
        'FC-T.S3',
    );
}

export class VisitasTrasiegoFormDefinition {
    private readonly formType!: FormType;

    private readonly showExtendedPestRows: boolean;

    public readonly lists: VisitasTrasiegoListsDefinition = new VisitasTrasiegoListsDefinition();

    public model: ReactiveFormValue<TrasiegoModel> = {} as ReactiveFormValue<TrasiegoModel>;

    public lastChangedFieldName: ReactiveFormChangedField<TrasiegoModel> = '';

    public formFields!: Form<TrasiegoModel>;

    public fieldNames: FormField<TrasiegoModel>[] = [];

    public fieldLabels: string[] = [];

    /**
     * Creates the trap visit form definition shared by edit forms and list modals.
     *
     * The definition centralizes visit fields, dropdown lists and hidden controls so
     * all trap visit modes use the same source of truth.
     *
     * @constructor
     * @public
     *
     * @param type - Request type used to define the form behaviour.
     * @param model - Optional trap visit model used as initial form data.
     * @param table - Optional table configuration used by table-based form modes.
     * @param showExtendedPestRows - Enables pest rows 7 to 10 when the feature flag is active.
     *
     * @returns A new trap visit form definition instance.
     */
    public constructor(
        type: FormType,
        model: Partial<TrasiegoModel> | null = {},
        table: (string[] | string[][])[] | null = null,
        showExtendedPestRows = false,
    ) {
        this.formType = +type;
        this.showExtendedPestRows = showExtendedPestRows;
        this.model = {
            ...this.model,
            ...(model ?? {}),
        } as ReactiveFormValue<TrasiegoModel>;
        this.normalizeModel();
        this.formFields = this.buildForm(table);
    }// constructor();

    /**
     * Updates the stored model and rebuilds visible section metadata.
     *
     * @public
     *
     * @param model - Partial trap visit model received from the reactive form.
     * @param lastChangedFieldName - Last field changed by the user or by the builder.
     *
     * @returns Nothing.
     */
    public updateModel(
        model: ReactiveFormValue<TrasiegoModel>,
        lastChangedFieldName: ReactiveFormChangedField<TrasiegoModel>,
    ): void {
        this.model = {
            ...this.model,
            ...model,
        } as ReactiveFormValue<TrasiegoModel>;
        this.lastChangedFieldName = lastChangedFieldName;

        if (lastChangedFieldName === 'id_finca') {
            this.clearInvalidParcelSelection();
        }// if();

        this.normalizeModel();
        this.refreshSections();
    }// updateModel();

    /**
     * Rebuilds section visibility and field metadata without replacing dropdown lists.
     *
     * @public
     *
     * @returns Nothing.
     */
    public refreshSections(): void {
        this.fieldNames = [];
        this.fieldLabels = [];
        this.formFields = {
            ...this.formFields,
            sections: this.getFormSections(),
        };
    }// refreshSections();

    /**
     * Builds and returns the main trap visit fields.
     *
     * @public
     *
     * @returns The trap visit fields sorted by display order.
     */
    public getVisitFields(): FormField<TrasiegoModel>[] {
        const visitFields: FormField<TrasiegoModel>[] = [
            this.farmField(),
            this.parcelField(),
            this.dropdownField('semana', 'Semana', 3, this.lists.semanas),
            this.dropdownField('ano', 'Año', 4, this.lists.anos),
            this.dropdownField('trampa', 'Trampa', 5, this.lists.trampas)
        ];

        this.appendVisibleFields(visitFields);
        return visitFields.sort((first, second) => (first.order ?? 0) - (second.order ?? 0));
    }// getVisitFields();

    /**
     * Builds and returns the main trap visit fields.
     *
     * @public
     *
     * @returns The trap visit fields sorted by display order.
     */
    public getPlagaFields(): FormField<TrasiegoModel>[] {
        const plagaFields: FormField<TrasiegoModel>[] = [];

        for (let index = 1; index <= this.getMaxPestRows(); index += 1) {
            const baseOrder = 8 + ((index - 1) * 4);

            plagaFields.push(this.dropdownField(`plaga${index}`, `Plaga #${index}`, baseOrder + 1, this.lists.plagas));
            plagaFields.push(this.numericField(`cantidad${index}`, `Cantidad #${index}`, baseOrder + 2));
        }// for();

        this.appendVisibleFields(plagaFields);
        return plagaFields.sort((first, second) => (first.order ?? 0) - (second.order ?? 0));
    }// getVisitFields();

    /**
     * Builds hidden fields required by submit and delete actions.
     *
     * @public
     *
     * @returns The hidden fields sorted by display order.
     */
    public getHiddenFields(): FormField<TrasiegoModel>[] {
        const hiddenFields: FormField<TrasiegoModel>[] = [
            {
                name: 'id',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                    defaultValue: this.getHiddenFieldDefaultValue('id'),
                },
                width: 0,
                conditions: this.conditions(),
                order: 1,
            },
        ];

        return hiddenFields.sort((first, second) => (first.order ?? 0) - (second.order ?? 0));
    }// getHiddenFields();

    /**
     * Builds the finca dropdown field for editable forms or the display field for modals.
     *
     * @private
     *
     * @returns Finca field definition.
     */
    private farmField(): FormField<TrasiegoModel> {
        return {
            name: this.getFarmFieldName(),
            vinculatedFields: undefined,
            label: 'Agrupación',
            input: this.isEditable()
                ? {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.fincas as DropdownData<unknown>,
                    dropdownConfiguration: { valuePrimaryKey: 'id' },
                    optionLabel: 'label',
                    optionValue: 'value',
                }
                : { type: InputType.ONLY_TEXT, isEditable: false },
            width: DEFAULT_FIELD_WIDTH,
            conditions: this.conditions({ isRequired: this.isEditable() }),
            order: 1,
        };
    }// farmField();

    /**
     * Builds the parcel dropdown field for editable forms or the display field for modals.
     *
     * @private
     *
     * @returns Parcel field definition.
     */
    private parcelField(): FormField<TrasiegoModel> {
        return {
            name: this.getParcelFieldName(),
            vinculatedFields: this.isEditable() ? ['id_finca'] : undefined,
            label: 'Subparcela',
            input: this.isEditable()
                ? {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.parcelas as DropdownData<unknown>,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (option: { value: unknown }): boolean => this.filterParcelOption(option.value as ParcelasModel | null),
                    },
                    optionLabel: 'label',
                    optionValue: 'value',
                }
                : { type: InputType.ONLY_TEXT, isEditable: false },
            dependencyBehaviour: {
                clearOnParentChange: false,
                keepValueUntilOptionsLoaded: true,
            },
            width: DEFAULT_FIELD_WIDTH,
            conditions: this.conditions({ isRequired: this.isEditable() }),
            order: 2,
        };
    }// parcelField();

    /**
     * Builds a dropdown field using the project defaults.
     *
     * @private
     *
     * @param name - Backend field name.
     * @param label - Visible field label.
     * @param order - Display order.
     * @param data - Dropdown values.
     *
     * @returns Dropdown field definition.
     */
    private dropdownField<TValue>(
        name: string,
        label: string,
        order: number,
        data: DropdownData<TValue>,
    ): FormField<TrasiegoModel> {
        return {
            name,
            vinculatedFields: undefined,
            label,
            input: this.isEditable()
                ? {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: data as DropdownData<unknown>,
                    optionLabel: 'label',
                    optionValue: 'value',
                }
                : { type: InputType.ONLY_TEXT, isEditable: false },
            width: this.isEditable() ? DEFAULT_FIELD_WIDTH : HALF_ROW_FIELD_WIDTH,
            conditions: this.conditions(),
            order,
        };
    }// dropdownField();

    /**
     * Builds a numeric field using the project defaults.
     *
     * @private
     *
     * @param name - Backend field name.
     * @param label - Visible field label.
     * @param order - Display order.
     *
     * @returns Numeric field definition.
     */
    private numericField(name: string, label: string, order: number): FormField<TrasiegoModel> {
        return {
            name,
            vinculatedFields: undefined,
            label,
            input: { type: this.isEditable() ? InputType.NUMERIC : InputType.ONLY_TEXT, isEditable: this.isEditable() },
            width: this.isEditable() ? DEFAULT_FIELD_WIDTH : HALF_ROW_FIELD_WIDTH,
            conditions: this.conditions(),
            order,
        };
    }// numericField();


    /**
     * Builds and returns all trap visit form sections.
     *
     * @private
     *
     * @returns The form sections sorted by display order.
     */
    private getFormSections(): FormSection<TrasiegoModel>[] {
        const sections: FormSection<TrasiegoModel>[] = [
            {
                name: '',
                fields: this.getVisitFields(),
                width: 12,
                isVisible: true,
                order: 1,
            },
            {
                name: '',
                fields: this.getPlagaFields(),
                width: 12,
                isVisible: true,
                order: 2,
            },
            {
                name: '',
                fields: this.getHiddenFields(),
                width: 12,
                isVisible: true,
                order: 3,
            },
        ];

        return sections.sort((first, second) => (first.order ?? 0) - (second.order ?? 0));
    }// getFormSections();

    /**
     * Builds the root form metadata object.
     *
     * @private
     *
     * @param table - Optional table configuration used by table-based form modes.
     *
     * @returns The root form metadata object.
     */
    private buildForm(table: (string[] | string[][])[] | null): Form<TrasiegoModel> {
        return {
            name: 'visitas-trasiego',
            header: this.getHeader(),
            type: this.formType,
            sections: this.getFormSections(),
            table: table === null
                ? null
                : { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
            uploadButton: { isEnabled: false, label: '' },
            uploadButtons: [],
            buttons: [],
            signed: false,
        };
    }// buildForm();

    /**
     * Creates field conditions using the project defaults.
     *
     * @private
     *
     * @param overrides - Partial condition values to override defaults.
     *
     * @returns A complete field conditions object.
     */
    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }// conditions();

    /**
     * Appends visible fields to table/export metadata arrays.
     *
     * @private
     *
     * @param fields - Fields to inspect and append when visible.
     *
     * @returns Nothing.
     */
    private appendVisibleFields(fields: FormField<TrasiegoModel>[]): void {
        const visibleFields = fields.filter((field) =>
            field.conditions.isVisible && field.input.type !== InputType.EMPTY && field.input.type !== InputType.HIDDEN,
        );

        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((field) => field.label));
    }// appendVisibleFields();

    /**
     * Returns the form header based on the active form mode.
     *
     * @private
     *
     * @returns Human-readable form header.
     */
    private getHeader(): string {
        switch (this.formType) {
            case FormType.CREATION:
                return 'Crear Visita';
            case FormType.DUPLICATION:
                return 'Duplicar Visita';
            case FormType.EDITION:
                return 'Actualizar Visita';
            case FormType.DELETION:
            case FormType.INFORMATIVE:
                return 'Datos del trasiego';
            default:
                return '';
        }// switch();
    }// getHeader();

    /**
     * Indicates whether normal fields can be edited in the current form mode.
     *
     * @private
     *
     * @returns True for creation, edition and duplication modes.
     */
    private isEditable(): boolean {
        return this.formType < FormType.DELETION;
    }// isEditable();

    /**
     * Returns the field name used for the farm depending on the form mode.
     *
     * @private
     *
     * @returns Backend id field in editable forms, display field in modal forms.
     */
    private getFarmFieldName(): string {
        return this.isEditable() ? 'id_finca' : 'finca_nombre';
    }// getFarmFieldName();

    /**
     * Returns the field name used for the parcel depending on the form mode.
     *
     * @private
     *
     * @returns Backend id field in editable forms, display field in modal forms.
     */
    private getParcelFieldName(): string {
        return this.isEditable() ? 'id_parcela' : 'parcela_nombre';
    }// getParcelFieldName();

    /**
     * Filters parcel options by the selected finca.
     *
     * @private
     *
     * @param parcel - Parcel option value.
     *
     * @returns True when the parcel must remain visible.
     */
    private filterParcelOption(parcel: ParcelasModel | null): boolean {
        const selectedFarmId = this.getSelectedFarmId();

        if (!selectedFarmId || !parcel) {
            return true;
        }// if();

        return selectedFarmId === this.normalizeIdentifier(parcel.id_finca);
    }// filterParcelOption();

    /**
     * Clears the selected parcel only when it does not belong to the selected finca.
     *
     * @private
     *
     * @returns Nothing.
     */
    private clearInvalidParcelSelection(): void {
        const selectedFarmId = this.getSelectedFarmId();

        if (!selectedFarmId) {
            this.model.id_parcela = null;
            return;
        }// if();

        const selectedParcelId = this.normalizeIdentifier(this.model.id_parcela);

        if (!selectedParcelId) {
            return;
        }// if();

        const selectedParcel = this.getSelectedParcel(selectedParcelId);

        if (!selectedParcel) {
            if (this.hasLoadedParcels()) {
                this.model.id_parcela = null;
            }// if();

            return;
        }// if();

        if (selectedFarmId !== this.normalizeIdentifier(selectedParcel.id_finca)) {
            this.model.id_parcela = null;
        }// if();
    }// clearInvalidParcelSelection();

    /**
     * Returns the currently selected farm id from the reactive model.
     *
     * @private
     *
     * @returns Selected farm id or null.
     */
    private getSelectedFarmId(): string | null {
        return this.normalizeIdentifier(this.model.id_finca);
    }// getSelectedFarmId();

    /**
     * Resolves the selected parcel from the model and loaded dropdown values.
     *
     * @private
     *
     * @param selectedParcelId - Parcel id to resolve.
     *
     * @returns Matching parcel or null.
     */
    private getSelectedParcel(selectedParcelId: string): ParcelasModel | null {
        const currentValue = (this.model as Record<string, unknown>)['id_parcela'];

        if (typeof currentValue === 'object' && currentValue !== null) {
            return currentValue as ParcelasModel;
        }// if();

        return this.lists.parcelas.values
            .map((option) => option.value)
            .find((parcel): parcel is ParcelasModel => this.normalizeIdentifier(parcel?.id) === selectedParcelId) ?? null;
    }// getSelectedParcel();

    /**
     * Indicates whether parcel options have been loaded from the backend.
     *
     * @private
     *
     * @returns True when at least one real parcel option exists.
     */
    private hasLoadedParcels(): boolean {
        return this.lists.parcelas.values.some((option) => option.value !== null && option.value !== undefined);
    }// hasLoadedParcels();

    /**
     * Returns the maximum pest rows visible for this installation.
     *
     * @private
     *
     * @returns The visible pest row count.
     */
    private getMaxPestRows(): number {
        return this.showExtendedPestRows ? EXTENDED_PEST_ROWS : DEFAULT_PEST_ROWS;
    }// getMaxPestRows();

    /**
     * Normalizes fields that are sensitive to backend display formats.
     *
     * @private
     *
     * @returns Nothing.
     */
    private normalizeModel(): void {
        this.normalizeNumericDropdownValue('semana');
        this.normalizeNumericDropdownValue('ano');
    }// normalizeModel();

    /**
     * Converts numeric dropdown values returned as strings into numbers.
     *
     * @private
     *
     * @param fieldName - Field to normalize.
     *
     * @returns Nothing.
     */
    private normalizeNumericDropdownValue(fieldName: 'semana' | 'ano'): void {
        const modelRecord = this.model as Record<string, unknown>;
        const value = modelRecord[fieldName];

        if (value === null || value === undefined || value === '') {
            modelRecord[fieldName] = null;
            return;
        }// if();

        const numericValue = Number(value);

        if (Number.isNaN(numericValue) || numericValue === 0) {
            modelRecord[fieldName] = null;
            return;
        }// if();

        modelRecord[fieldName] = numericValue;
    }// normalizeNumericDropdownValue();

    /**
     * Reads a scalar hidden field default value from the current model.
     *
     * @private
     *
     * @param name - Hidden field name.
     *
     * @returns The default value for the hidden field.
     */
    private getHiddenFieldDefaultValue(name: string): string | number | boolean | null | undefined {
        const value = (this.model as Record<string, unknown>)[name];

        if (value === null || value === undefined) {
            return null;
        }// if();

        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return value;
        }// if();

        return String(value);
    }// getHiddenFieldDefaultValue();

    /**
     * Normalizes scalar ids and dropdown option objects into comparable ids.
     *
     * @private
     *
     * @param value - Raw value to inspect.
     *
     * @returns Normalized id or null.
     */
    private normalizeIdentifier(value: unknown): string | null {
        if (value === null || value === undefined) {
            return null;
        }// if();

        if (typeof value === 'object') {
            const record = value as Record<string, unknown>;
            const id = record['id'] ?? record['value'];

            return this.normalizeIdentifier(id);
        }// if();

        const normalized = String(value).trim();

        return normalized === '' || normalized === 'null' || normalized === 'undefined' ? null : normalized;
    }// normalizeIdentifier();
}// class;
